// AudioRecorder.ts — v2: accumulate session audio + message-based transcripts
// Flush on generationComplete, finalize() as fallback

export type MessageRole = 'ai-tutor' | 'user' | 'tool' | 'system';

export interface ToolCallInfo {
  name: string;
  args: Record<string, unknown>;
  result: Record<string, unknown>;
}

export interface RoundMessage {
  role: MessageRole;
  text: string;
  timestamp: number;
  toolCall?: ToolCallInfo;
}

export interface RoundSummary {
  roundIndex: number;
  messages: RoundMessage[];
  startTime: number;
  endTime: number;
}

export interface TurnSnapshot {
  roundIndex: number;
  rounds: RoundSummary[];
  sessionAudioWav: Blob;
}

export interface FinalizedSession {
  sessionId: string;
  userId?: string;
  userEmail?: string;
  startTime: number;
  endTime: number;
  duration: number;
  totalRounds: number;
  rounds: RoundSummary[];
  fullSessionWav: Blob;
  systemPrompt?: string;
  provider?: string;
  posthogRecordingUrl?: string;
}

export interface AudioRecorderOptions {
  userId?: string;
  userEmail?: string;
  systemPrompt?: string;
  startTime?: number;
  onTurnSnapshot?: (snapshot: TurnSnapshot) => void;
}

const MODEL_SAMPLE_RATE = 24000;
const OUTPUT_SAMPLE_RATE = 16000;

type AudioChunkEntry =
  | { source: 'user'; data: Int16Array }
  | { source: 'model'; data: Float32Array };

export class AudioRecorder {
  private sessionId: string;
  private userId?: string;
  private userEmail?: string;
  private systemPrompt?: string;
  private startTime: number;

  // Single timeline: chunks in arrival order, tagged by source
  private timeline: AudioChunkEntry[] = [];

  // Message list for current round (reset per round)
  private currentMessages: RoundMessage[] = [];

  // Round tracking
  private rounds: RoundSummary[] = [];
  private roundIndex = 0;
  private roundStartTime: number;

  private _provider?: string;
  private _posthogRecordingUrl?: string;
  private onTurnSnapshot?: (snapshot: TurnSnapshot) => void;

  set provider(value: string | undefined) {
    this._provider = value;
  }

  set posthogRecordingUrl(value: string | undefined) {
    this._posthogRecordingUrl = value;
  }

  constructor(sessionId: string, options?: AudioRecorderOptions) {
    this.sessionId = sessionId;
    this.userId = options?.userId;
    this.userEmail = options?.userEmail;
    this.systemPrompt = options?.systemPrompt;
    this.startTime = options?.startTime ?? Date.now();
    this.roundStartTime = this.startTime;
    this.onTurnSnapshot = options?.onTurnSnapshot;
  }

  appendUserAudio(pcmData: Int16Array): void {
    this.timeline.push({ source: 'user', data: pcmData });
  }

  appendModelAudio(float32Data: Float32Array): void {
    this.timeline.push({ source: 'model', data: float32Data });
  }

  appendUserTranscript(text: string): void {
    this.appendTranscript('user', text);
  }

  appendModelTranscript(text: string): void {
    this.appendTranscript('ai-tutor', text);
  }

  appendSystemEvent(text: string): void {
    this.currentMessages.push({ role: 'system', text, timestamp: Date.now() });
  }

  appendToolCall(name: string, args: Record<string, unknown>, result: Record<string, unknown>): void {
    this.currentMessages.push({
      role: 'tool',
      text: name,
      timestamp: Date.now(),
      toolCall: { name, args, result },
    });
  }

  private appendTranscript(role: MessageRole, text: string): void {
    if (!text) return;
    const now = Date.now();
    const last = this.currentMessages[this.currentMessages.length - 1];
    if (last && last.role === role) {
      // User ASR chunks carry their own spacing; tutor text is parser-cleaned (trimmed)
      last.text += role === 'user' ? text : ` ${text}`;
      last.timestamp = now;
    } else {
      this.currentMessages.push({ role, text, timestamp: now });
    }
  }

  /** Collapse whitespace and trim a transcript string */
  private static cleanTranscript(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
  }

  /** Return a copy of currentMessages with cleaned transcript text */
  private cleanedMessages(): RoundMessage[] {
    return this.currentMessages.map((m) =>
      m.role === 'tool' ? { ...m } : { ...m, text: AudioRecorder.cleanTranscript(m.text) },
    );
  }

  /** Called on turnComplete — no-op in v2; generationComplete is the flush signal */
  onTurnComplete(): void {}

  /** Called on generationComplete — flush current round + encode cumulative WAV */
  onGenerationComplete(): void {
    const now = Date.now();
    const round: RoundSummary = {
      roundIndex: this.roundIndex,
      messages: this.cleanedMessages(),
      startTime: this.roundStartTime,
      endTime: now,
    };

    this.rounds.push(round);
    this.currentMessages = [];
    this.roundIndex++;
    this.roundStartTime = now;

    const sessionWav = this.encodeSessionWav();
    const snapshot: TurnSnapshot = {
      roundIndex: round.roundIndex,
      rounds: [...this.rounds],
      sessionAudioWav: sessionWav,
    };

    this.onTurnSnapshot?.(snapshot);
  }

  /** Fallback: flush any remaining data and return finalized session */
  finalize(): FinalizedSession {
    const now = Date.now();

    // If there's un-flushed messages, create one more round
    if (this.currentMessages.length > 0) {
      this.rounds.push({
        roundIndex: this.roundIndex,
        messages: this.cleanedMessages(),
        startTime: this.roundStartTime,
        endTime: now,
      });
      this.currentMessages = [];
    }

    return {
      sessionId: this.sessionId,
      userId: this.userId,
      userEmail: this.userEmail,
      startTime: this.startTime,
      endTime: now,
      duration: now - this.startTime,
      totalRounds: this.rounds.length,
      rounds: [...this.rounds],
      fullSessionWav: this.encodeSessionWav(),
      systemPrompt: this.systemPrompt,
      provider: this._provider,
      posthogRecordingUrl: this._posthogRecordingUrl,
    };
  }

  // --------------- WAV encoding ---------------

  /**
   * Encode all accumulated audio into a mono 16kHz 16bit WAV.
   *
   * Chunks are processed in arrival order (the timeline), preserving the
   * natural interleaving of user and model audio. Model audio (24kHz Float32)
   * is resampled to 16kHz Int16 before appending.
   */
  private encodeSessionWav(): Blob {
    // Calculate total output samples
    let totalSamples = 0;
    for (const entry of this.timeline) {
      if (entry.source === 'user') {
        totalSamples += entry.data.length;
      } else {
        // 24kHz → 16kHz: output length = floor(input / 1.5)
        totalSamples += Math.floor(entry.data.length / (MODEL_SAMPLE_RATE / OUTPUT_SAMPLE_RATE));
      }
    }

    const pcm = new Int16Array(totalSamples);
    let offset = 0;

    for (const entry of this.timeline) {
      if (entry.source === 'user') {
        pcm.set(entry.data, offset);
        offset += entry.data.length;
      } else {
        const resampled = resample24kTo16k(entry.data);
        pcm.set(resampled, offset);
        offset += resampled.length;
      }
    }

    return createWavBlob(pcm, OUTPUT_SAMPLE_RATE);
  }
}

// --------------- Helpers ---------------

/** Resample a single Float32 chunk from 24kHz to 16kHz Int16 via linear interpolation */
function resample24kTo16k(input: Float32Array): Int16Array {
  if (input.length === 0) return new Int16Array(0);

  const ratio = MODEL_SAMPLE_RATE / OUTPUT_SAMPLE_RATE; // 1.5
  const outLen = Math.floor(input.length / ratio);
  const out = new Int16Array(outLen);

  for (let o = 0; o < outLen; o++) {
    const srcPos = o * ratio;
    const srcIdx = Math.floor(srcPos);
    const frac = srcPos - srcIdx;
    const s0 = input[srcIdx] ?? 0;
    const s1 = input[srcIdx + 1] ?? 0;
    const val = s0 + frac * (s1 - s0);
    out[o] = Math.max(-32768, Math.min(32767, Math.round(val * 32767)));
  }

  return out;
}

/** Create a WAV Blob from Int16 PCM data */
function createWavBlob(pcm: Int16Array, sampleRate: number): Blob {
  const byteRate = sampleRate * 2; // mono, 16bit
  const dataSize = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeStr(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(view, 8, 'WAVE');

  // fmt sub-chunk
  writeStr(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);  // PCM
  view.setUint16(22, 1, true);  // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, 2, true);  // blockAlign
  view.setUint16(34, 16, true); // bitsPerSample

  // data sub-chunk
  writeStr(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  new Uint8Array(buffer, 44).set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength));

  return new Blob([buffer], { type: 'audio/wav' });
}

function writeStr(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
